

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MdClose } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const CustomerTable = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [qaStatus, setQaStatus] = useState('');
  const [summary, setSummary] = useState('');
  const [qaComments, setQaComments] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]);
  const recordsPerPage = 8;
  const maxVisiblePages = 5;

  // Fetch records from backend
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/call-records/records`);
        setRecords(response.data);
        console.log("responseresponse" ,response);
        
      } catch (error) {
        console.error('Error fetching records:', error);
        toast.error('Failed to load records: ' + (error.response?.data?.error || error.message));
        setRecords([])
      }
    };
    fetchRecords();
  }, []);

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = records.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(records.length / recordsPerPage);

  const getPageNumbers = () => {
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const openPopup = (call) => {
    console.log('Full audio URL:', call.audioUrl);
    setIsPopupOpen(true);
    setSelectedCall(call);
    setQaStatus(call.qaStatus || '')
    setQaComments(call.qaComments || '');
    setIsVerified(call.isVerified);
  };

  const closePopup = () => {
    
    // toast.dismiss(); 
    setIsPopupOpen(false);
    setSelectedCall(null);
    setQaStatus('');
    setQaComments('');
    setIsVerified(false);
    setDropdownOpen(false);
  };


  const handleUpdate = async () => {
    if (!selectedCall || !qaStatus || !qaComments.trim() || !isVerified) {
      toast.error('Please fill all required fields!');
      return;
    }
  
    setUpdating(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_SERVER_API}/api/call-records/update`, {
        id: selectedCall.id,
        qaStatus,
        qaComments, // Include qaComments here
        isVerified: true,
      });
     
      // Fallback to alert if toast doesn't work
      setTimeout(() => {
        toast.success('Record updated successfully!', {
          toastId: 'update-success',
          autoClose: 5000, // 5 seconds to ensure visibility
          position: 'top-center', // Change position to test visibility
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }, 1000);
      const updatedRecords = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/call-records/records`);
      setRecords(updatedRecords.data);
      console.log('Triggering success toast');
      
      setTimeout(closePopup, 500);
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Failed to update record: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };
  // Handle audio error (e.g., expired URL)
  const handleAudioError = (e) => {
    console.error('Audio playback error:', e);
    toast.error('Failed to play recording. The URL may have expired.');
  };

  const ToastErrorBoundary = ({ children }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      const handleError = (error) => {
        console.error('Toast Error:', error);
        setHasError(true);
      };
      window.addEventListener('error', handleError);
      return () => window.removeEventListener('error', handleError);
    }, []);

    if (hasError) {
      return <div className="text-red-500">Toast error occurred. Please refresh.</div>;
    }
    return children;
  };

  const cleanName = (name) => {
    return name ? name.replace(/\.mp3$/i, '') : 'Unnamed';
  };

  return (
    <div className="w-full h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] overflow-y-auto hide-scrollbar">
      {/* Scrollable Table Container */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full bg-white border border-gray-200 rounded-lg shadow-md min-w-[1200px]">
          <thead className="sticky top-0 bg-blue-400 text-white">
            <tr>
              <th className="py-3 px-3 text-center border-b">SR</th>
              <th className="py-3 px-3 text-center border-b">Name</th>
              <th className="py-3 px-3 text-center border-b">Agent Sentiment</th>
              <th className="py-3 px-3 text-center border-b">QA Status</th>
              <th className="py-3 px-3 text-center border-b">QA Comments</th>
              <th className="py-3 px-3 text-center border-b">Is Verified</th>
              <th className="py-3 px-3 text-center border-b">View Summary</th>
            </tr>
          </thead>
          <tbody>
          {currentRecords.length > 0 ? (
            currentRecords.map((call, index) => (
              call ? (
              <motion.tr
                key={call.id}
                className="hover:bg-gray-100 even:bg-gray-50 transition-all duration-200 ease-in-out"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <td className="py-3 px-3 border-b text-center align-middle">{indexOfFirstRecord + index + 1}</td>
                <td className="py-3 px-3 border-b text-center align-middle max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                  {cleanName(call.name)}
                </td>
                <td className="py-3 px-4 border-b text-center align-middle">
                  <span
                    className={`px-3 py-1 rounded-lg text-sm inline-block ${
                      call.agentSentiment === 'Positive'
                        ? 'bg-green-100 text-green-700'
                        : call.agentSentiment === 'Negative'
                        ? 'bg-red-100 text-red-700'
                        : call.agentSentiment === 'Neutral'
                        ? 'bg-yellow-100 text-yellow-700'
                        : call.agentSentiment === 'Negative/DND'
                        ? 'bg-red-100 text-orange-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {call.agentSentiment}
                  </span>
                </td>
                <td className="py-3 px-4 border-b text-center align-middle">
                  <span
                    className={`px-3 py-1 rounded-lg text-sm inline-block ${
                      call.qaStatus === 'Positive'
                        ? 'bg-green-100 text-green-700'
                        : call.qaStatus === 'Negative'
                        ? 'bg-red-100 text-red-700'
                        : call.qaStatus === 'Neutral'
                        ? 'bg-yellow-100 text-yellow-700'
                        : call.qaStatus === 'Negative/DND'
                        ? 'bg-red-100 text-orange-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                  {call.qaStatus}
                  </span>
                </td>
                <td className="py-3 px-3 border-b text-center align-middle">
                  <div className="max-h-20 overflow-y-auto whitespace-pre-wrap break-words">{call.qaComments}</div>
                </td>
                <td className="py-3 px-3 border-b text-center align-middle">
                  {call.isVerified ? 'Yes' : 'No'}
                </td>
                <td className="px-3 py-2 border-b text-center align-middle">
                  <button
                    className="px-3 py-2 bg-blue-400 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
                    onClick={() => openPopup(call)}
                  >
                    View Summary
                  </button>
                </td>
                </motion.tr>
                ) : null
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-3 px-3 text-center text-gray-500">
                  No records available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-6 px-6">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
            currentPage === 1
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div className="flex items-center gap-2">
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => paginate(page)}
              className={`px-3 py-1 rounded-md font-medium transition-all duration-200 ${
                currentPage === page
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
            currentPage === totalPages
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          Next
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {isPopupOpen && selectedCall && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex justify-center items-center z-50">
          <motion.div
            className="bg-white/80 backdrop-blur-lg p-10 rounded-3xl w-11/12 max-w-4xl shadow-3xl relative flex flex-col md:grid md:grid-cols-2 gap-10 border border-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <button
              className="absolute top-6 right-6 text-gray-500 hover:text-red-600 transition-all duration-300"
              onClick={closePopup}
              aria-label="Close popup"
            >
              <MdClose className="text-3xl" />
            </button>

            <div className="flex flex-col gap-8">
              <h2 className="text-4xl font-extrabold text-gray-800 tracking-wide">
                Edit QA Details
              </h2>
              <div className="relative">
                <div
                  className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300 rounded-2xl p-4 text-lg text-gray-700 cursor-pointer flex justify-between items-center shadow-lg hover:shadow-xl transition-all"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {qaStatus || 'Choose QA Status'}
                  <svg
                    className={`w-6 h-6 transform transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {dropdownOpen && (
                  <div className="absolute mt-2 bg-white/90 border border-gray-300 rounded-xl shadow-lg w-full z-10 overflow-hidden backdrop-blur-md">
                    {['Positive', 'Negative', 'Neutral', 'Negative/DND', 'Not Attended'].map((option) => (
                      <div
                        key={option}
                        className="p-4 hover:bg-gradient-to-r from-blue-50 to-blue-100 text-gray-800 cursor-pointer transition-all duration-300"
                        onClick={() => {
                          setQaStatus(option);
                          setDropdownOpen(false);
                        }}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-700">Add Comments</h2>
                <textarea
                  className="mt-2 p-4 w-full h-24 border border-gray-300 rounded-2xl bg-gray-50 text-gray-700 text-lg leading-relaxed shadow-inner focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none whitespace-pre-wrap break-words"
                  placeholder="Add Comments here..."
                  value={qaComments}
                  onChange={(e) => setQaComments(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isVerified}
                  onChange={(e) => setIsVerified(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="text-gray-700">Verify Record (Required)</label>
              </div>

              <button
                className={`px-6 py-2 w-40 text-lg font-semibold rounded-full transition-all duration-300 ${
                  updating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-400 text-white hover:bg-blue-600'
                }`}
                onClick={handleUpdate}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Update'}
              </button>

              <div>
                <h2 className="text-4xl font-extrabold text-gray-800 tracking-wide">Call Recording</h2>
                {selectedCall.audioUrl ? (
                  <audio
                    className="w-full mt-4 rounded-full border border-gray-300 shadow-md"
                    controls
                    src={selectedCall.audioUrl}
                    onError={handleAudioError}
                  >
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <p className="text-gray-600 mt-2">No recording available.</p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <h2 className="text-4xl font-extrabold text-gray-800 tracking-wide">Call Transcript </h2>
              <div className="flex-grow p-6 border border-gray-300 rounded-2xl bg-gray-50/80 text-gray-700 text-lg leading-relaxed overflow-auto max-h-96 shadow-inner whitespace-pre-wrap break-words">
                {selectedCall.transcriptWithoutPI || 'No transcript available.'}
              </div>
            </div>
          </motion.div>
        </div>
      )}

<ToastErrorBoundary>
        <ToastContainer autoClose={3000} /> {/* Set a reasonable autoClose time */}
      </ToastErrorBoundary>
    </div>
  );
};

export default CustomerTable;